// Shared Junior Crew angler character: options, local cache, and helpers
// used by the Character Creator, the Zone Map and every game screen.

import { readJSON, writeJSON } from "@/lib/junior-crew/storage";

export const CHARACTER_KEY = "jrcrew:character";
export const CREW_CODE_KEY = "jrcrew:crew-code";
export const ZONE_PROGRESS_KEY = "jrcrew:zone-progress";

export const AVATARS = ["🧒", "👧", "🧑", "👦", "🧕", "👱", "🐬", "🦈", "🐢", "🦀", "🐟", "🐙"];
export const HATS = ["🧢", "🎣", "⛑️", "👒", "🎩", "⛵"];

export const COLORS: { id: string; label: string; art: string; strip: string; hex: string }[] = [
  { id: "ocean", label: "Ocean Blue", art: "from-sky-400 to-blue-800", strip: "bg-sky-400", hex: "#38bdf8" },
  { id: "sunset", label: "Sunset Orange", art: "from-amber-400 to-orange-700", strip: "bg-amber-400", hex: "#fbbf24" },
  { id: "kelp", label: "Kelp Green", art: "from-emerald-400 to-green-800", strip: "bg-emerald-400", hex: "#34d399" },
  { id: "coral", label: "Coral Pink", art: "from-rose-400 to-red-800", strip: "bg-rose-400", hex: "#fb7185" },
  { id: "midnight", label: "Midnight Purple", art: "from-fuchsia-400 to-purple-900", strip: "bg-fuchsia-400", hex: "#e879f9" },
  { id: "sand", label: "Sandy Gold", art: "from-yellow-300 to-amber-700", strip: "bg-yellow-300", hex: "#fde047" },
];

export const RODS = [
  { id: "pier", label: "Pier Pro", emoji: "🎡" },
  { id: "surf", label: "Surf Master", emoji: "🏄" },
  { id: "kayak", label: "Kayak Ninja", emoji: "🛶" },
  { id: "boat", label: "Offshore Captain", emoji: "🚤" },
];

export const TITLES = ["Deckhand", "First Mate", "Reel Rookie", "Cast Champion", "Tide Tracker", "Wave Rider"];

/** Real saltwater clothing kids actually wear on the water. */
export const OUTFITS = [
  { id: "upf-hoodie", label: "UPF 50+ Hooded Sun Shirt", emoji: "🥼", note: "Long sleeves beat sunburn on a long day." },
  { id: "bibs", label: "Foul-Weather Bibs", emoji: "🧥", note: "Keeps spray and fish slime off on a wet deck." },
  { id: "rash-guard", label: "Rash Guard & Boardshorts", emoji: "👕", note: "Great for warm bay days and kayak trips." },
  { id: "pfd-vest", label: "Inflatable PFD Vest", emoji: "🦺", note: "A life jacket is the number one piece of gear." },
  { id: "wader-jacket", label: "Surf Waders & Wading Jacket", emoji: "🩳", note: "Stay warm standing in the surf line." },
];

/** Real rod-and-reel combos matched to California fishing styles. */
export const SETUPS = [
  { id: "pier-combo", label: "9 ft Pier Combo · 4000 Spinner", emoji: "🎣", style: "pier", note: "20 lb braid for mackerel, perch and bass." },
  { id: "surf-combo", label: "10 ft Surf Rod · 5000 Spinner", emoji: "🌊", style: "surf", note: "Long casts past the breakers for perch and halibut." },
  { id: "kayak-combo", label: "7 ft Kayak Baitcaster", emoji: "🛶", style: "kayak", note: "Short and tough for tight casts over the flats." },
  { id: "boat-jig", label: "Offshore Jig Stick · Star Drag", emoji: "🚤", style: "boat", note: "Built for yellowtail and surface iron." },
  { id: "bottom-rig", label: "Rockfish Bottom Rod · Conventional", emoji: "🪨", style: "boat", note: "Heavy gear for deep reef rockfish and lingcod." },
];

export const SHADES = [
  { id: "polarized", label: "Polarized Amber", emoji: "🕶️", note: "Cuts glare so you can see fish under the surface." },
  { id: "mirror", label: "Blue Mirror Lenses", emoji: "😎", note: "Bright offshore days." },
  { id: "clear", label: "Clear Safety Glasses", emoji: "👓", note: "Protects eyes from flying hooks and sinkers." },
  { id: "none", label: "No Shades", emoji: "🚫", note: "Sunscreen and a hat brim still required." },
];

export const BOOTS = [
  { id: "deck-shoes", label: "Non-Slip Deck Shoes", emoji: "👟", note: "Grip on a wet boat deck." },
  { id: "surf-boots", label: "Neoprene Surf Boots", emoji: "🥾", note: "Warm feet and protection in the wash." },
  { id: "jetty-boots", label: "Studded Jetty Boots", emoji: "🧗", note: "Studs bite into slick rocks." },
  { id: "sandals", label: "Strap Sandals", emoji: "🩴", note: "Bay and kayak days only — never a jetty." },
];

/** Crew emblem symbols drawn as SVG so they read well over the ocean. */
export const EMBLEMS = [
  { id: "anchor", label: "Anchor" },
  { id: "marlin", label: "Marlin" },
  { id: "wave", label: "Wave" },
  { id: "compass", label: "Compass" },
  { id: "hook", label: "Hook" },
  { id: "trident", label: "Trident" },
] as const;

export type EmblemId = (typeof EMBLEMS)[number]["id"];

export type Character = {
  name: string;
  avatar: string;
  hat: string;
  color: string;
  rod: string;
  title: string;
  homeZone?: string;
  outfit: string;
  setup: string;
  emblem: string;
  shades: string;
  boots: string;
};

export const DEFAULT_CHARACTER: Character = {
  name: "",
  avatar: AVATARS[0],
  hat: HATS[0],
  color: "ocean",
  rod: "pier",
  title: TITLES[0],
  outfit: OUTFITS[0].id,
  setup: SETUPS[0].id,
  emblem: "anchor",
  shades: SHADES[0].id,
  boots: BOOTS[0].id,
};

export function outfitOf(c: Character) {
  return OUTFITS.find((x) => x.id === c.outfit) ?? OUTFITS[0];
}
export function setupOf(c: Character) {
  return SETUPS.find((x) => x.id === c.setup) ?? SETUPS[0];
}
export function shadesOf(c: Character) {
  return SHADES.find((x) => x.id === c.shades) ?? SHADES[0];
}
export function bootsOf(c: Character) {
  return BOOTS.find((x) => x.id === c.boots) ?? BOOTS[0];
}

export function colorOf(c: Character) {
  return COLORS.find((x) => x.id === c.color) ?? COLORS[0];
}
export function rodOf(c: Character) {
  return RODS.find((x) => x.id === c.rod) ?? RODS[0];
}
export function displayName(c: Character) {
  return c.name.trim() || "New Recruit";
}

export function readCharacter(): Character {
  return { ...DEFAULT_CHARACTER, ...readJSON<Character>(CHARACTER_KEY, DEFAULT_CHARACTER) };
}
export function writeCharacter(c: Character) {
  writeJSON(CHARACTER_KEY, c);
}

/** A short device-local code that identifies this kid's saved character. */
export function getCrewCode(): string {
  if (typeof window === "undefined") return "";
  let code = window.localStorage.getItem(CREW_CODE_KEY) ?? "";
  if (!/^[A-Z0-9]{10}$/.test(code)) {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    const bytes = new Uint32Array(10);
    crypto.getRandomValues(bytes);
    code = Array.from(bytes, (b) => chars[b % chars.length]).join("");
    window.localStorage.setItem(CREW_CODE_KEY, code);
  }
  return code;
}
export function setCrewCode(code: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(CREW_CODE_KEY, code.trim().toUpperCase());
}

/** Zone simulation progress: best catch rate bonus earned per zone. */
export type ZoneProgress = Record<string, { casts: number; catches: number; best: string; bonus: number }>;

export function readZoneProgress(): ZoneProgress {
  return readJSON<ZoneProgress>(ZONE_PROGRESS_KEY, {});
}
export function writeZoneProgress(p: ZoneProgress) {
  writeJSON(ZONE_PROGRESS_KEY, p);
}
/** Total bonus (%) the player has unlocked in the zone simulator, capped. */
export function totalZoneBonus(p: ZoneProgress = readZoneProgress()): number {
  return Math.min(
    25,
    Object.values(p).reduce((sum, z) => sum + (z.bonus || 0), 0),
  );
}

/**
 * Query string appended to every game (iframe or React) so each player's
 * character travels into the game with them.
 */
export function playerQuery(c: Character = readCharacter(), bonus = totalZoneBonus()): string {
  const col = colorOf(c);
  const params = new URLSearchParams({
    player: displayName(c),
    title: c.title,
    avatar: c.avatar,
    hat: c.hat,
    color: col.id,
    colorHex: col.hex,
    style: rodOf(c).id,
    outfit: c.outfit,
    setup: c.setup,
    emblem: c.emblem,
    shades: c.shades,
    boots: c.boots,
    bonus: String(bonus),
  });
  return params.toString();
}

export function withPlayer(src: string, c?: Character): string {
  const q = playerQuery(c);
  return src.includes("?") ? `${src}&${q}` : `${src}?${q}`;
}
